import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Star, Phone } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: 'easeOut' },
};

const HERO_IMAGE = 'https://images.unsplash.com/photo-1487530811015-780a14da99e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHx3ZWRkaW5nJTIwZmxvd2Vyc3xlbnwwfHx8fDE3NDk2MDcxNzN8MA&ixlib=rb-4.1.0&q=85&w=2000';

const ABOUT_IMAGE = 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/img-8384_1.jpg';
const ABOUT_IMAGE2 = 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/published/alpimages1_1.jpg';

const boutiquesData = [
  {
    name: 'Martigny',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/published/devant.jpeg',
    address: 'Rue des Petits-Epineys 3, 1920 Martigny',
    since: '70+ ans',
  },
  {
    name: 'Verbier',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/vitrine_orig.jpeg',
    address: 'Rte des Creux 6, 1936 Verbier',
    since: 'Depuis 2021',
  },
  {
    name: 'Sion',
    image: 'https://www.lesnocesdeginkgo.ch/uploads/1/3/4/0/134055628/v2_orig.jpeg',
    address: 'Av. du Ritz 33, 1950 Sion',
    since: 'Depuis 2026',
  },
];

const reviews = [
  { name: 'Marie-Claire B.', text: 'Des créations d\'une beauté et d\'une fraîcheur incomparables. Le bouquet de mariée était absolument magnifique !', rating: 5 },
  { name: 'Thomas F.', text: 'Équipe passionnée, à l\'écoute, et des fleurs toujours fraîches. Je recommande les yeux fermés.', rating: 5 },
  { name: 'Sarah & Marc', text: 'La décoration florale de notre mariage dépassait tous nos espoirs. Un travail exceptionnel, merci !', rating: 5 },
];

const Home = () => {
  const { t } = useTranslation();

  useEffect(() => { document.title = 'Les Noces de Ginkgo — Artisan Fleuriste'; }, []);

  return (
    <div className="font-sans bg-cream">

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section data-testid="hero-section" className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ginkgo-900/50 via-ginkgo-900/30 to-ginkgo-900/70" />

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-white/70 text-xs tracking-[0.3em] uppercase font-sans font-medium mb-6"
          >
            {t('home.hero_tagline')}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9, ease: 'easeOut' }}
            className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white font-medium leading-[1.05] tracking-tight mb-8"
          >
            Les Noces<br /><span className="italic font-light">de Ginkgo</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="text-white/75 text-sm sm:text-base font-light mb-10"
          >
            {t('home.welcome_subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/commandes"
              data-testid="hero-cta-order"
              className="bg-white text-ginkgo-800 px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-gold hover:text-white transition-all duration-300"
            >
              {t('home.hero_cta')}
            </Link>
            <Link
              to="/galerie"
              data-testid="hero-cta-gallery"
              className="border border-white/60 text-white px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-white/10 transition-all duration-300"
            >
              {t('home.hero_secondary')}
            </Link>
          </motion.div>

          {/* Reviews badge */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.6 }}
            className="mt-12 flex items-center justify-center gap-2"
          >
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={12} fill="#C5A065" color="#C5A065" />
              ))}
            </div>
            <span className="text-white/70 text-xs">{t('home.reviews_count')}</span>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8 }}
            className="w-px h-12 bg-gradient-to-b from-white/50 to-transparent"
          />
        </div>
      </section>

      {/* ── CITATION ─────────────────────────────────────────────── */}
      <section data-testid="citation-section" className="bg-cream-dark py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-12 h-px bg-gold mx-auto mb-8" />
          <motion.blockquote {...fadeUp}>
            <p className="font-serif text-2xl md:text-3xl text-ginkgo-800 italic font-light leading-relaxed">
              "{t('home.citation')}"
            </p>
          </motion.blockquote>
          <div className="w-12 h-px bg-gold mx-auto mt-8" />
        </div>
      </section>

      {/* ── ABOUT / WELCOME ──────────────────────────────────────── */}
      <section data-testid="about-section" className="bg-cream py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div {...fadeUp}>
              <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-4">
                {t('home.welcome_subtitle')}
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-ginkgo-800 font-medium mb-6 leading-tight">
                {t('home.welcome_title')}
              </h2>
              <p className="text-stone-600 text-base leading-relaxed mb-4">
                {t('home.welcome_text')}
              </p>
              <p className="text-stone-600 text-base leading-relaxed mb-8">
                {t('home.welcome_text2')}
              </p>
              <Link
                to="/commandes"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 bg-ginkgo-800 text-white px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors duration-300"
              >
                {t('home.order_online')}
                <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-3">
                <img
                  src={ABOUT_IMAGE}
                  alt="Les Noces de Ginkgo — fleurs"
                  className="w-full h-72 object-cover"
                  loading="lazy"
                />
                <img
                  src={ABOUT_IMAGE2}
                  alt="Les Noces de Ginkgo — création florale"
                  className="w-full h-72 object-cover mt-8"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-gold/30 hidden lg:block" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BOUTIQUES ────────────────────────────────────────────── */}
      <section data-testid="boutiques-section" className="bg-ginkgo-50 py-20 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-medium mb-3">Valais, Suisse</p>
            <h2 className="font-serif text-4xl md:text-5xl text-ginkgo-800 font-medium">
              {t('home.boutiques_title')}
            </h2>
            <p className="mt-3 text-stone-500 text-base max-w-lg mx-auto">{t('home.boutiques_subtitle')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {boutiquesData.map((b, i) => (
              <motion.div
                key={b.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="group relative overflow-hidden bg-white border border-stone-100 hover:shadow-xl transition-all duration-500"
                data-testid={`boutique-card-${b.name.toLowerCase()}`}
              >
                <div className="overflow-hidden h-64">
                  <img
                    src={b.image}
                    alt={`Boutique ${b.name}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-serif text-2xl text-ginkgo-800">{b.name}</h3>
                    <span className="text-xs text-gold font-medium tracking-wide">{b.since}</span>
                  </div>
                  <p className="text-stone-500 text-sm mb-5">{b.address}</p>
                  <Link
                    to="/contact"
                    data-testid={`boutique-cta-${b.name.toLowerCase()}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ginkgo-800 font-medium hover:text-gold transition-colors"
                  >
                    {t('home.boutiques_cta')}
                    <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="text-center mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/commandes"
              data-testid="boutiques-order-cta"
              className="bg-ginkgo-800 text-white px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-900 transition-colors"
            >
              {t('home.order_online')}
            </Link>
            <a
              href="tel:+41277222750"
              data-testid="boutiques-phone-cta"
              className="inline-flex items-center gap-2 border border-ginkgo-800 text-ginkgo-800 px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-ginkgo-50 transition-colors"
            >
              <Phone size={13} />
              {t('home.order_phone')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── REVIEWS ──────────────────────────────────────────────── */}
      <section data-testid="reviews-section" className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-14">
            <div className="flex justify-center mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} fill="#C5A065" color="#C5A065" />)}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-ginkgo-800 font-medium">
              {t('home.reviews_title')}
            </h2>
            <p className="mt-2 text-stone-500 text-sm">{t('home.reviews_count')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="bg-white border border-stone-100 p-8 relative"
                data-testid={`review-card-${i}`}
              >
                <div className="flex mb-4">
                  {[...Array(review.rating)].map((_, j) => (
                    <Star key={j} size={12} fill="#C5A065" color="#C5A065" />
                  ))}
                </div>
                <p className="text-stone-600 text-sm leading-relaxed italic mb-6">"{review.text}"</p>
                <p className="text-ginkgo-800 text-xs font-medium uppercase tracking-wider">{review.name}</p>
                <div className="absolute top-6 right-6 text-gold/20 font-serif text-6xl leading-none select-none">"</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STORY BANNER (Alma -> NDG) ────────────────────────────── */}
      <section data-testid="story-section" className="bg-ginkgo-800 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <p className="text-gold text-xs uppercase tracking-[0.3em] font-medium mb-4">
              {t('home.new_sion')}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-white font-light italic mb-6">
              Alma Fleurs devient<br />Les Noces de Ginkgo
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-2xl mx-auto mb-8">
              {t('home.alma_story')}
            </p>
            <Link
              to="/boutique"
              data-testid="story-cta"
              className="inline-flex items-center gap-2 border border-gold text-gold px-8 py-4 text-xs uppercase tracking-widest font-medium hover:bg-gold hover:text-white transition-all duration-300"
            >
              {t('home.discover')}
              <ArrowRight size={13} />
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Home;
